// store/brandNameSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

/**
 * AsyncThunk для генерации названия бренда через сокет.
 * Параметры: activity, audience, wordCount, promptIndex и socket.
 * Ожидается, что в ответе от сервера в случае успеха поле brandName содержит массив строк
 * либо строку в формате "[Name1, Name2, ...]".
 */
export const generateBrandName = createAsyncThunk(
  'brandName/generate',
  async ({ activity, audience, wordCount, promptIndex, socket }, { rejectWithValue }) => {
    const MAX_RETRIES = 3;
    let lastError;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const response = await new Promise((resolve) => {
          socket.emit('generate_brand', { activity, audience, wordCount, promptIndex }, (response) => {
            resolve(response);
          });
        });
        if (response.status === 'ok') {
          let brandNames = response.brandName;
          // Если пришла строка, пытаемся её распарсить
          if (typeof brandNames === 'string') {
            brandNames = brandNames.trim();
            if (brandNames.startsWith('[') && brandNames.endsWith(']')) {
              brandNames = brandNames.slice(1, -1); // удаляем квадратные скобки
              brandNames = brandNames.split(',').map(item => item.trim()); // разбиваем по запятой и убираем пробелы
              brandNames = brandNames.filter(item => item.length > 0); // исключаем пустые элементы
            }
          }
          // Проверяем, что получили массив строк
          if (Array.isArray(brandNames) && brandNames.every(item => typeof item === 'string')) {
            return { brandNames, wordCount };
          } else {
            lastError = 'Неверный формат ответа: brandName не является массивом строк';
          }
        } else {
          lastError = response.message || 'Ошибка при генерации названия';
        }
      } catch (error) {
        lastError = error.message || 'Ошибка генерации';
      }
    }
    return rejectWithValue(lastError || 'Ошибка генерации бренда');
  }
);

const brandNameSlice = createSlice({
  name: 'brandName',
  initialState: {
    // Добавляем поле locked для хранения заблокированных названий
    oneWord: { brandNames: [], locked: [], loading: false, error: null },
    twoWord: { brandNames: [], locked: [], loading: false, error: null },
    // Новое поле для хранения результатов поиска похожих названий
    similar: {} // ключ – id карточки, значение: { loading, error, data }
  },
  reducers: {
    clearOneWord: (state) => {
      state.oneWord = { brandNames: [], locked: state.oneWord.locked, loading: false, error: null };
    },
    clearTwoWord: (state) => {
      state.twoWord = { brandNames: [], locked: state.twoWord.locked, loading: false, error: null };
    },
    // Редюсеры для поиска похожих названий
    similarBrandSearchStart: (state, action) => {
        const suggestionId = action.payload;
        state.similar[suggestionId] = { loading: true, error: null, data: null };
      },
    similarBrandSearchSuccess: (state, action) => {
      const { suggestionId, data } = action.payload;
      state.similar[suggestionId] = { loading: false, error: null, data };
    },
    similarBrandSearchError: (state, action) => {
      const { suggestionId, error } = action.payload;
      state.similar[suggestionId] = { loading: false, error, data: [] };
    },
    clearSimilarBrandResults: (state, action) => {
      const suggestionId = action.payload;
      delete state.similar[suggestionId];
    },
    // Добавляем редюсеры для блокировки/разблокировки названий
    lockBrandName: (state, action) => {
      const { wordCount, name } = action.payload;
      if (wordCount === 1) {
        if (!state.oneWord.locked.includes(name)) {
          state.oneWord.locked.push(name);
        }
      } else if (wordCount === 2) {
        if (!state.twoWord.locked.includes(name)) {
          state.twoWord.locked.push(name);
        }
      }
    },
    unlockBrandName: (state, action) => {
      const { wordCount, name } = action.payload;
      if (wordCount === 1) {
        state.oneWord.locked = state.oneWord.locked.filter(n => n !== name);
      } else if (wordCount === 2) {
        state.twoWord.locked = state.twoWord.locked.filter(n => n !== name);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateBrandName.pending, (state, action) => {
        const { wordCount } = action.meta.arg;
        if (wordCount === 1) {
          state.oneWord.loading = true;
          state.oneWord.error = null;
        } else if (wordCount === 2) {
          state.twoWord.loading = true;
          state.twoWord.error = null;
        }
      })
      .addCase(generateBrandName.fulfilled, (state, action) => {
        const { wordCount, brandNames } = action.payload;
        if (wordCount === 1) {
          // Фильтруем имена, которые уже заблокированы
          const newNames = brandNames.filter(name => !state.oneWord.locked.includes(name));
          state.oneWord.brandNames = newNames;
          state.oneWord.loading = false;
        } else if (wordCount === 2) {
          const newNames = brandNames.filter(name => !state.twoWord.locked.includes(name));
          state.twoWord.brandNames = newNames;
          state.twoWord.loading = false;
        }
      })
      .addCase(generateBrandName.rejected, (state, action) => {
        const { wordCount } = action.meta.arg;
        if (wordCount === 1) {
          state.oneWord.error = action.payload || action.error.message;
          state.oneWord.loading = false;
        } else if (wordCount === 2) {
          state.twoWord.error = action.payload || action.error.message;
          state.twoWord.loading = false;
        }
      });
  },
});

export const { 
  clearOneWord, 
  clearTwoWord, 
  similarBrandSearchStart, 
  similarBrandSearchSuccess, 
  similarBrandSearchError, 
  clearSimilarBrandResults,
  lockBrandName,
  unlockBrandName,
} = brandNameSlice.actions;
export default brandNameSlice.reducer;
